// Per-project database configuration API (/api/project-db).
//
// Named database connections (Postgres / Oracle / SQLite) — including the
// optional 'preview_runtime' connection that preview & run use instead of
// the tenant preview DB — plus the project engine settings (db_mode /
// biz_logic). Passwords are write-only: never returned by the API.

import apiClient from './client'

export type ProjectDbType = 'postgres' | 'oracle' | 'sqlite'
export type ProjectDbRole = 'preview_runtime' | 'general' | 'sync_target'

/**
 * How much of a provided database the project adopts.
 *  - 'source'    — the database BECOMES the project's own; its tables are bound
 *                  in place (managed:false) and both applications share the
 *                  rows. Requires the 'preview_runtime' role.
 *  - 'structure' — only the table STRUCTURE is reused; deploy creates the
 *                  tables in the project's own database and the app owns them.
 */
export type AdoptionMode = 'source' | 'structure'

export interface ProjectDbConnection {
  id: string
  name: string
  db_type: ProjectDbType
  role: ProjectDbRole
  adoption_mode?: AdoptionMode
  host?: string | null
  port?: number | null
  database_name?: string | null
  oracle_connect_type?: 'service_name' | 'sid' | null
  username?: string | null
  has_password: boolean
  sqlite_path?: string | null
  db_schema?: string | null
  options?: string | null
}

export interface ProjectDbConnectionInput {
  project: string
  id?: string
  name: string
  db_type: ProjectDbType
  role: ProjectDbRole
  // Omit to leave the stored mode unchanged.
  adoption_mode?: AdoptionMode
  host?: string
  port?: number
  database_name?: string
  oracle_connect_type?: 'service_name' | 'sid'
  username?: string
  // Omit to keep the stored password; '' clears it.
  password?: string
  sqlite_path?: string
  db_schema?: string
}

export interface ResolvedPreviewDb {
  source: 'project_connection' | 'user_settings' | 'platform_default'
  db_type: ProjectDbType
  connection_name?: string
  summary: string
}

export interface EngineSettings {
  db_mode: 'postgres' | 'sqlite' | 'oracle'
  biz_logic: 'plpgsql' | 'plsql' | 'python' | 'java'
  effective_db_mode?: string
}

// Summary of a project's generated business logic — drives the
// dialect-migration warning when the preview/run database is switched to a
// different engine.
export interface BusinessLogicSummary {
  current_dialect: ProjectDbType
  current_biz_logic: 'plpgsql' | 'plsql' | 'python' | 'java'
  has_generated_logic: boolean
  function_count: number
  objects: string[]
  languages: string[]
}

// The natural business-logic language for each database engine (the first
// allowed language for that dialect). Mirrors the backend _VALID_BIZ_LOGIC /
// biz_logic_lang._DB_LANG_COMBOS matrix. Postgres defaults to Python — the
// platform default, which runs on every engine.
export const DEFAULT_BIZ_LOGIC: Record<ProjectDbType, EngineSettings['biz_logic']> = {
  postgres: 'python',
  oracle: 'plsql',
  sqlite: 'python',
}
// Oracle + Java is legal as a PROJECT setting but executes on the J2EE
// (WildFly) runtime only — the Python runtime's Java sidecar holds a
// Postgres-only JDBC pool, so live preview and the downloadable runtime
// resolve those functions to "no rule". The bundle build is what refuses it,
// per target flavour (runtime_bundle_builder._validate_runtime_config).
const VALID_BIZ_LOGIC: Record<ProjectDbType, EngineSettings['biz_logic'][]> = {
  postgres: ['python', 'plpgsql', 'java'],
  oracle: ['plsql', 'python', 'java'],
  sqlite: ['python'],
}

// Combos that only the J2EE runtime can execute — the UI says so where the
// language is picked, so "Java on Oracle" is a deliberate choice rather than
// a setting that quietly stops working in preview.
export function bizLogicNeedsJ2ee(
  db: ProjectDbType, biz: EngineSettings['biz_logic'],
): boolean {
  return db === 'oracle' && biz === 'java'
}

// Is the given business-logic language usable on the given database engine?
export function bizLogicAllowedOn(db: ProjectDbType, biz: EngineSettings['biz_logic']): boolean {
  return VALID_BIZ_LOGIC[db]?.includes(biz) ?? false
}

export async function listProjectDbConnections(project: string): Promise<ProjectDbConnection[]> {
  const res = await apiClient.get('/project-db/connections', { params: { project } })
  return res.data?.connections ?? []
}

export async function saveProjectDbConnection(
  body: ProjectDbConnectionInput,
): Promise<ProjectDbConnection> {
  const res = await apiClient.post('/project-db/connections', body)
  return res.data?.connection
}

export async function deleteProjectDbConnection(
  project: string, connectionId: string,
): Promise<boolean> {
  const res = await apiClient.delete(`/project-db/connections/${encodeURIComponent(connectionId)}`, {
    params: { project },
  })
  return Boolean(res.data?.deleted)
}

export async function testProjectDbConnection(
  body: ProjectDbConnectionInput & { connection_id?: string },
): Promise<{ success: boolean; message: string }> {
  const res = await apiClient.post('/project-db/test-connection', body)
  return res.data
}

export async function getResolvedPreviewDb(project: string): Promise<ResolvedPreviewDb> {
  const res = await apiClient.get('/project-db/resolved', { params: { project } })
  return res.data
}

export async function getEngineSettings(project: string): Promise<EngineSettings> {
  const res = await apiClient.get('/project-db/engine-settings', { params: { project } })
  return res.data
}

export async function putEngineSettings(
  project: string, settings: Partial<EngineSettings>,
): Promise<EngineSettings> {
  const res = await apiClient.put('/project-db/engine-settings', { project, ...settings })
  return res.data
}

export async function getProjectBusinessLogicSummary(
  project: string,
): Promise<BusinessLogicSummary> {
  const res = await apiClient.get('/project-db/business-logic-summary', { params: { project } })
  return res.data
}
